import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  CssBaseline,
  ThemeProvider,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  Fade,
} from '@mui/material';
import {
  Email,
  Phone,
  LocationOn,
  Send,
  Store,
  ContactMail,
  Person,
} from '@mui/icons-material';
import createAppTheme from '../theme/theme';
import translations from '../i18n/translations';
import AppHeader from '../components/AppHeader';
import AppFooter from '../components/AppFooter';
import MobileDrawer from '../components/MobileDrawer';
import axios from 'axios';

const BASE_URL = 'https://dhahabjw.com';

const ContactUs = () => {
  const [language, setLanguage] = useState(localStorage.getItem('lang') || 'en');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [submitting, setSubmitting] = useState(false);
  const t = translations[language];
  const theme = createAppTheme(true, language === 'ar');
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = t.nameRequired || 'Name is required';
    }
    if (!formData.email.trim()) {
      errors.email = t.emailRequired || 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t.emailInvalid || 'Invalid email address';
    }
    if (!formData.message.trim()) {
      errors.message = t.messageRequired || 'Message is required';
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${BASE_URL}/api/contact`, formData, {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 10000,
      });

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: t.messageSent || 'Message sent successfully!',
          severity: 'success',
        });
        setFormData({ name: '', email: '', message: '' });
      } else {
        throw new Error(response.data.error || 'Failed to send message');
      }
    } catch (error) {
      let errorMessage = t.messageSendError || 'Failed to send message';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = t.timeout || 'Request timeout. Please try again.';
      } else if (!error.response) {
        errorMessage = t.networkError || 'Network error. Please check your connection.';
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
        <AppHeader
          language={language}
          setLanguage={setLanguage}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          t={t}
        />
        <MobileDrawer language={language} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} t={t} />

        <Container maxWidth="xl" sx={{ py: { xs: 3, sm: 4, md: 6 } }}>
          <Fade in timeout={500}>
            <Box>
              {/* Hero Section */}
              <Box
                sx={{
                  textAlign: 'center',
                  mb: { xs: 3, sm: 4, md: 6 },
                  px: { xs: 2, sm: 0 },
                }}
              >
                <Typography
                  variant={{ xs: 'h4', sm: 'h3', md: 'h2' }}
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 2,
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                  }}
                >
                  {t.contactUsTitle || 'Contact Us'}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    maxWidth: 800,
                    mx: 'auto',
                    lineHeight: 1.8,
                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  }}
                >
                  {t.contactUsDescription ||
                    'We are here to assist you. Reach out with any questions, feedback, or inquiries, and our team will get back to you promptly.'}
                </Typography>
              </Box>

              <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                {/* Contact Form */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.01) 100%)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                      <Typography
                        variant={{ xs: 'h6', sm: 'h5' }}
                        sx={{
                          fontWeight: 700,
                          mb: 2,
                          fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        }}
                      >
                        {t.sendMessage || 'Send Us a Message'}
                      </Typography>
                      <Box
                        component="form"
                        onSubmit={handleSubmit}
                        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
                      >
                        <TextField
                          name="name"
                          label={t.name || 'Name'}
                          value={formData.name}
                          onChange={handleInputChange}
                          error={!!formErrors.name}
                          helperText={formErrors.name}
                          fullWidth
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'rgba(26, 26, 26, 0.8)',
                              borderRadius: 2,
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Person sx={{ color: 'text.secondary', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          name="email"
                          label={t.email || 'Email'}
                          value={formData.email}
                          onChange={handleInputChange}
                          error={!!formErrors.email}
                          helperText={formErrors.email}
                          fullWidth
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'rgba(26, 26, 26, 0.8)',
                              borderRadius: 2,
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            },
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Email sx={{ color: 'text.secondary', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                              </InputAdornment>
                            ),
                          }}
                        />
                        <TextField
                          name="message"
                          label={t.message || 'Message'}
                          value={formData.message}
                          onChange={handleInputChange}
                          error={!!formErrors.message}
                          helperText={formErrors.message}
                          fullWidth
                          multiline
                          rows={4}
                          size="small"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'rgba(26, 26, 26, 0.8)',
                              borderRadius: 2,
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            },
                          }}
                        />
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          startIcon={<Send />}
                          disabled={submitting}
                          sx={{
                            bgcolor: 'primary.main',
                            color: 'black',
                            borderRadius: 3,
                            px: { xs: 2, sm: 3 },
                            py: { xs: 1, sm: 1.5 },
                            fontWeight: 700,
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            '&:hover': {
                              bgcolor: 'primary.dark',
                              transform: { xs: 'none', sm: 'translateY(-2px)' },
                              boxShadow: { xs: theme.shadows[4], sm: theme.shadows[8] },
                            },
                          }}
                        >
                          {submitting ? (t.sending || 'Sending...') : (t.send || 'Send')}
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Contact Information */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.01) 100%)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                      <Typography
                        variant={{ xs: 'h6', sm: 'h5' }}
                        sx={{
                          fontWeight: 700,
                          mb: 2,
                          fontSize: { xs: '1.25rem', sm: '1.5rem' },
                        }}
                      >
                        {t.contactInfo || 'Contact Information'}
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={2}>
                        <Box display="flex" alignItems="flex-start" gap={1}>
                          <LocationOn
                            sx={{
                              color: 'primary.main',
                              fontSize: { xs: '1.5rem', sm: '2rem' },
                              mt: 0.2,
                            }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              lineHeight: 1.6,
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            }}
                          >
                            {t.address || '123 Jewelry Street, Kuwait City, Kuwait'}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Phone
                            sx={{
                              color: 'primary.main',
                              fontSize: { xs: '1.5rem', sm: '2rem' },
                            }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            }}
                          >
                            {t.phone || '+965 1234 5678'}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Email
                            sx={{
                              color: 'primary.main',
                              fontSize: { xs: '1.5rem', sm: '2rem' },
                            }}
                          />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            }}
                          >
                            {t.email || 'support@dhahabjw.com'}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Call to Action */}
              <Box
                sx={{
                  textAlign: 'center',
                  mt: { xs: 3, sm: 4, md: 6 },
                  mb: { xs: 3, sm: 4 },
                }}
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Store />}
                  onClick={() => navigate('/store')}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'black',
                    borderRadius: 3,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 1.5 },
                    fontWeight: 700,
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      transform: { xs: 'none', sm: 'translateY(-2px)' },
                      boxShadow: { xs: theme.shadows[4], sm: theme.shadows[8] },
                    },
                  }}
                >
                  {t.exploreStore || 'Explore Our Store'}
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          sx={{
            '& .MuiSnackbarContent-root': {
              fontSize: { xs: '0.875rem', sm: '1rem' },
              maxWidth: { xs: '90vw', sm: 'auto' },
            },
          }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{
              width: '100%',
              fontSize: { xs: '0.875rem', sm: '1rem' },
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <AppFooter t={t} language={language} />
      </Box>
    </ThemeProvider>
  );
};

export default ContactUs;